import * as React from 'react';
import { type PressEvent, type PressHookProps, useObjectRef } from 'react-aria';
import {
    type OverlayTriggerProps as StatelyOverlayTriggerProps,
    useOverlayTriggerState as useStatelyOverlayTriggerState,
} from 'react-stately';

import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import type { OverlayRootProps } from './OverlayRoot';

export type OverlayTriggerStateProps = StatelyOverlayTriggerProps & {
    /**
     * Ref to the trigger element.
     */
    ref?: React.MutableRefObject<HTMLElement | null>;
};

// Mimic the API of react-stately's OverlayTriggerState
// but their API has a problem with this because of function definitions.
export type OverlayTriggerState = {
    /** The ID of the overlay target element. */
    targetId: string;
    /** Ref to the trigger element. */
    ref: React.MutableRefObject<HTMLElement | null>;

    /**
     * Props to pass to the root element of the overlay.
     */
    rootProps: Omit<OverlayRootProps, 'children'>;

    // Props from react-stately's OverlayTriggerState

    /** Whether the overlay is currently open. */
    readonly isOpen: boolean;
    /** Sets whether the overlay is open. */
    setOpen: (isOpen: boolean) => void;
    /** Opens the overlay. */
    open: () => void;
    /** Closes the overlay. */
    close: () => void;
    /** Toggles the overlay's visibility. */
    toggle: () => void;
};

/**
 * Hook to get the state for an overlay trigger.
 */
export function useOverlayTriggerState(props?: OverlayTriggerStateProps): OverlayTriggerState {
    const targetId = React.useId();
    const triggerRef = useObjectRef<HTMLElement>(props?.ref);
    const { close, isOpen, open, setOpen, toggle } = useStatelyOverlayTriggerState(props ?? {});
    return React.useMemo(
        () => ({
            close,
            isOpen,
            open,
            setOpen,
            toggle,
            targetId,
            ref: triggerRef,
            rootProps: {
                isOpen: isOpen,
                onOpenChange: setOpen,
                id: targetId,
                triggerRef,
            },
        }),
        [close, isOpen, open, setOpen, toggle, targetId, triggerRef]
    );
}

export const OverlayTriggerContext = React.createContext<OverlayTriggerState | null>(null);

export type OverlayTriggerProviderProps = {
    trigger: OverlayTriggerState;
    children: React.ReactNode;
};

export function OverlayTriggerProvider(props: OverlayTriggerProviderProps) {
    return (
        <OverlayTriggerContext.Provider value={props.trigger}>
            {props.children}
        </OverlayTriggerContext.Provider>
    );
}

export type OverlayTriggerProps = {
    /**
     * The trigger state for the overlay. If not provided, the component must be used within a OverlayRoot.
     */
    trigger?: OverlayTriggerState;
    /**
     * The children to render. Must be a single React element.
     */
    children: React.ReactNode;
    /**
     * The value of the `aria-haspopup` attribute for the trigger element.
     */
    ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
};

/**
 * A component that wraps a trigger element and opens an overlay when the trigger is activated.
 */
export function OverlayTrigger(props: OverlayTriggerProps) {
    const child = React.Children.only(props.children);
    assert(
        React.isValidElement<
            { onPress: PressHookProps['onPress'] } & React.AriaAttributes & {
                    id: string;
                    ref: React.RefCallback<HTMLElement | null>;
                }
        >(child),
        'OverlayTrigger must have a single React element as its child.'
    );

    const context = React.useContext(OverlayTriggerContext);

    const trigger = props.trigger ?? context;
    if (!trigger) {
        throw new Error(
            'OverlayTrigger must be used within a OverlayTriggerProvider or with a `trigger` prop.'
        );
    }

    const mergeRef = useMergedRefs<HTMLElement>(trigger.ref, (child as any).ref);

    return React.cloneElement(child, {
        ref: mergeRef,
        onPress: (event: PressEvent) => {
            child.props.onPress?.(event);
            trigger.open();
        },
        'aria-haspopup': props.ariaHasPopup ?? 'true',
        'aria-expanded': trigger.isOpen,
        'aria-controls': trigger.targetId,
    });
}

/**
 * Hook to get the close function for the current overlay trigger.
 */
export function useOverlayClose() {
    const context = React.useContext(OverlayTriggerContext);
    assert(context, 'useOverlayClose must be used within a OverlayTriggerProvider.');
    return context.close;
}

/**
 * Hook to get the ref for the current overlay trigger.
 */
export function useOverlayTriggerRef<T extends HTMLElement = HTMLElement>(): React.RefObject<T> {
    const context = React.useContext(OverlayTriggerContext);
    assert(context, 'useOverlayTriggerRef must be used within a OverlayTriggerProvider.');

    // @ts-expect-error
    return context.ref;
}
